import axios from "../utils/AxiosInstance";
import { ResponseMessage } from "../models/ResponseMessage";
import { Constants } from "../constants/constants";
import {
  ActivateSubscriptionRequest,
  CancelSubscriptionRequest,
  ExtendSubscriptionRequest,
  OrganizationSubscription,
  OrgEntitlements,
  Plan,
} from "../models/SubscriptionModels";

const API_BASE_URL = Constants.API_BASE_URL;

export async function getPublicPlansAsync(
  token: string,
): Promise<ResponseMessage<Plan[]>> {
  const response = await axios.get(`${API_BASE_URL}/subscriptions/plans`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

/**
 * Anonymous variant for the public marketing/pricing pages.
 * The endpoint is AllowAnonymous — no auth header is sent.
 */
export async function getPublicPlansAnonymousAsync(): Promise<
  ResponseMessage<Plan[]>
> {
  const response = await axios.get(`${API_BASE_URL}/subscriptions/plans`);
  const message = ResponseMessage.fromJson<Plan[]>(response.data);

  // Tolerate the alternate `{ data, statusCode }` envelope shape.
  if (!Array.isArray(message.result) && Array.isArray(response.data?.data)) {
    message.result = response.data.data;
    message.success = true;
  }

  return message;
}

export async function getEntitlementsAsync(
  token: string,
  organizationId: string,
): Promise<ResponseMessage<OrgEntitlements>> {
  const response = await axios.get(
    `${API_BASE_URL}/subscriptions/entitlements/${organizationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function activateSubscriptionAsync(
  token: string,
  request: ActivateSubscriptionRequest,
): Promise<ResponseMessage<OrganizationSubscription>> {
  const response = await axios.post(
    `${API_BASE_URL}/subscriptions/activate`,
    request,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function extendSubscriptionAsync(
  token: string,
  request: ExtendSubscriptionRequest,
): Promise<ResponseMessage<OrganizationSubscription>> {
  const response = await axios.post(
    `${API_BASE_URL}/subscriptions/extend`,
    request,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function cancelSubscriptionAsync(
  token: string,
  request: CancelSubscriptionRequest,
): Promise<ResponseMessage<boolean>> {
  const response = await axios.post(
    `${API_BASE_URL}/subscriptions/cancel`,
    request,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}